import { apiClient } from '@/api/client';
import type {
  AccountResponse,
  CreatePaymentPlanRequest,
  DownPaymentResponse,
  PaginatedAccounts,
  PaymentPlanHistory,
  PaymentPlanResponse,
  RecordDownPaymentRequest,
  UpdateAccountRequest,
  UpdateDownPaymentRequest,
  UpdateInstallmentRequest,
} from '@/types/shared';

export interface ListAccountsParams {
  page?: number;
  pageSize?: number;
  /** `field:ASC` / `field:DESC` — allowed fields per accounts.service.ts `applySort`:
   * `createdAt`, `totalAgreedFees`, `studentCode`, `nameAr`, `nameEn`. */
  sort?: string;
  search?: string;
}

/** GET /accounts — Roles: Admin, Accounts Officer, Admissions Officer, Student Affairs Officer,
 * Sales Manager. */
export async function listAccounts(params: ListAccountsParams): Promise<PaginatedAccounts> {
  const { data } = await apiClient.get<PaginatedAccounts>('/accounts', { params });
  return data;
}

/** GET /accounts/:studentCode */
export async function getAccount(studentCode: string): Promise<AccountResponse> {
  const { data } = await apiClient.get<AccountResponse>(
    `/accounts/${encodeURIComponent(studentCode)}`,
  );
  return data;
}

/** PATCH /accounts/:studentCode — Roles: Admin, Accounts Officer. Sets totalAgreedFees and/or
 * installmentCount and/or a full `installments[]` plan replace. D6 (sum(installments) ===
 * totalAgreedFees) is enforced server-side — 422 INSTALLMENTS_SUM_MISMATCH otherwise. */
export async function updateAccount(
  studentCode: string,
  payload: UpdateAccountRequest,
): Promise<AccountResponse> {
  const { data } = await apiClient.patch<AccountResponse>(
    `/accounts/${encodeURIComponent(studentCode)}`,
    payload,
  );
  return data;
}

/** PATCH /accounts/:studentCode/installments/:installmentNo — Roles: Admin, Accounts Officer.
 * Edits a single installment's amount/dueDate (rejected once fully Paid — 422
 * PAID_INSTALLMENT_LOCKED) and/or its payment metadata (always editable). Returns the full,
 * refreshed `AccountResponse` so totals stay in sync after a payment is recorded. */
export async function updateInstallment(
  studentCode: string,
  installmentNo: number,
  payload: UpdateInstallmentRequest,
): Promise<AccountResponse> {
  const { data } = await apiClient.patch<AccountResponse>(
    `/accounts/${encodeURIComponent(studentCode)}/installments/${installmentNo}`,
    payload,
  );
  return data;
}

/** GET /accounts/:studentCode/plans — Roles: READ_ROLES. Lightweight plan history (all plans for
 * the student, summaries only — no installments[], desc by planNumber). Powers the plan-history
 * switcher on `AccountsDetailPage` (11-accounts-multi-plan.md §5.1, T60). */
export async function listPlans(studentCode: string): Promise<PaymentPlanHistory> {
  const { data } = await apiClient.get<PaymentPlanHistory>(
    `/accounts/${encodeURIComponent(studentCode)}/plans`,
  );
  return data;
}

/** GET /accounts/:studentCode/plans/:planNumber — Roles: READ_ROLES. A specific plan, full shape
 * (installments included). Archived plans are viewable this way but are always read-only — no
 * write route ever targets a non-Active plan. */
export async function getPlan(
  studentCode: string,
  planNumber: number,
): Promise<PaymentPlanResponse> {
  const { data } = await apiClient.get<PaymentPlanResponse>(
    `/accounts/${encodeURIComponent(studentCode)}/plans/${planNumber}`,
  );
  return data;
}

/** POST /accounts/:studentCode/plans — Roles: Admin, Accounts Officer. Archives the student's
 * current Active plan and creates a new, blank Active plan (fee 0 / DEFAULT_INSTALLMENT_COUNT
 * installments unless overridden by `payload`). Refused with 422 ACTIVE_PLAN_NOT_COMPLETED unless
 * the current Active plan is fully paid (11-accounts-multi-plan.md §4.2) — the caller should gate
 * the triggering UI on `isCompleted` and treat this rejection as defense-in-depth only. */
export async function createPlan(
  studentCode: string,
  payload?: CreatePaymentPlanRequest,
): Promise<PaymentPlanResponse> {
  const { data } = await apiClient.post<PaymentPlanResponse>(
    `/accounts/${encodeURIComponent(studentCode)}/plans`,
    payload ?? {},
  );
  return data;
}

/** GET /accounts/:studentCode/down-payment — Roles: accounts READ_ROLES. `null` when none recorded. */
export async function getDownPayment(studentCode: string): Promise<DownPaymentResponse | null> {
  const { data } = await apiClient.get<DownPaymentResponse | null>(
    `/accounts/${encodeURIComponent(studentCode)}/down-payment`,
  );
  return data;
}

/** POST /accounts/:studentCode/down-payment — Roles: accounts WRITE_ROLES. Recorded once per
 * student; a second call returns 409 DOWN_PAYMENT_EXISTS (first time only). */
export async function recordDownPayment(
  studentCode: string,
  payload: RecordDownPaymentRequest,
): Promise<DownPaymentResponse> {
  const { data } = await apiClient.post<DownPaymentResponse>(
    `/accounts/${encodeURIComponent(studentCode)}/down-payment`,
    payload,
  );
  return data;
}

/** PATCH /accounts/:studentCode/down-payment — Roles: accounts WRITE_ROLES. Correct a recorded one. */
export async function updateDownPayment(
  studentCode: string,
  payload: UpdateDownPaymentRequest,
): Promise<DownPaymentResponse> {
  const { data } = await apiClient.patch<DownPaymentResponse>(
    `/accounts/${encodeURIComponent(studentCode)}/down-payment`,
    payload,
  );
  return data;
}

// 2026-07: GET /accounts/:studentCode/referral removed — per-student referral cost is superseded by
// per-partner cost plans (see cost-plans.api.ts).
